import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { addFundComplianceAlternatives, addStockComplianceAlternatives, clearComplianceAlternatives } from '@/redux/modules/compliance-alternatives';
import { addFundToPortfolioById, addStockToPortfolioById, updatePortfolioHolding } from '@/redux/modules/edit-portfolio';

// Components
import DefaultModal from '@/views/components/modals/DefaultModal';
import InstrumentComparisonTable from '../compliance-comparison/InstrumentComparisonTable';

import LoadingSpinner from '@/views/components/common/LoadingSpinner';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import { ShelfStatus as ShelfStatusEnum } from '@/utils/shelf-management';

// Types
import Fund from '@/types/fund';
import PortfolioHolding from '@/types/portfolio-holding';
import Stock from '@/types/stock';
import { DefaultModalProps } from '@/views/components/modals/DefaultModal';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatMessage } from '@/utils/i18n';
import { HOLDING_TYPES } from '@/utils/portfolios';
import { showError } from '@/utils/errors';

type DispatchProps = {
  addFundComplianceAlternatives: (id: string) => Promise<string>;
  addStockComplianceAlternatives: (id: string) => Promise<string>;
  addFundToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding) => Promise<void>;
  addStockToPortfolioById: (id: string, holdingToReplace?: PortfolioHolding) => Promise<void>;
  clearComplianceAlternatives: () => void;
  updatePortfolioHolding: (portfolioHolding: PortfolioHolding) => void;
};

type StateProps = {
  complianceAlternatives: Fund[] | Stock[];
};

type Props = DefaultModalProps &
  DispatchProps &
  StateProps & {
    portfolioHolding: PortfolioHolding;
    closeModal: () => void;
  };

const CompareAlternatives = (props: Props) => {
  const {
    complianceAlternatives,
    portfolioHolding,
    show,
    addFundComplianceAlternatives,
    addStockComplianceAlternatives,
    addFundToPortfolioById,
    addStockToPortfolioById,
    clearComplianceAlternatives,
    closeModal,
    updatePortfolioHolding,
    ...rest
  } = props;

  // Variables
  const i18nBasePath = 'Modal.CompareAlternatives';
  const [isGeneratingAlternatives, setIsGeneratingAlternatives] = useState<boolean>(false);

  // Trigger the calculation of the alternatives
  useEffect(() => {
    if (show) {
      generateAlternatives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Reset "isGeneratingAlternatives" if "complianceAlternatives" is not empty
  useEffect(() => {
    if (complianceAlternatives.length) {
      setIsGeneratingAlternatives(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceAlternatives]);

  const generateAlternatives = async () => {
    if (!portfolioHolding.original.id) {
      return;
    }

    setIsGeneratingAlternatives(true);
    const addComplianceAlternatives = portfolioHolding.original.type === HOLDING_TYPES.FUND ? addFundComplianceAlternatives : addStockComplianceAlternatives;

    try {
      const complianceComparisonId = await addComplianceAlternatives(portfolioHolding.original.id);

      if (complianceComparisonId) {
        updatePortfolioHolding({
          ...portfolioHolding,
          complianceComparisons: [...(portfolioHolding.complianceComparisons || []), complianceComparisonId],
          flags: portfolioHolding.flags?.filter(flag => flag !== ShelfStatusEnum.ON_SHELF),
        });
      }
      setIsGeneratingAlternatives(false);
    } catch (err) {
      setIsGeneratingAlternatives(false);
      showError(err);
      closeModal();
    }
  };

  const onSelectComplianceAlternative = (instrument: Fund | Stock) => {
    const addInstrumentToPortfolio = portfolioHolding.original.type === HOLDING_TYPES.FUND ? addFundToPortfolioById : addStockToPortfolioById;

    addInstrumentToPortfolio(instrument._id)
      .then(() => closeModal())
      .catch(showError);
  };

  const renderCompareAlternativeTable = (): JSX.Element => {
    if (isGeneratingAlternatives || !complianceAlternatives.length) {
      return <LoadingSpinner message={formatMessage(`${i18nBasePath}.LoadingMessage`)} />;
    }

    switch (portfolioHolding.original.type) {
      case HOLDING_TYPES.FUND:
        return (
          <InstrumentComparisonTable<Fund>
            complianceAlternatives={complianceAlternatives as Fund[]}
            instrumentType={HOLDING_TYPES.FUND}
            onSelect={onSelectComplianceAlternative}
            clearComplianceAlternatives={clearComplianceAlternatives}
          />
        );

      case HOLDING_TYPES.STOCK:
        return (
          <InstrumentComparisonTable<Stock>
            complianceAlternatives={complianceAlternatives as Stock[]}
            instrumentType={HOLDING_TYPES.STOCK}
            onSelect={onSelectComplianceAlternative}
            clearComplianceAlternatives={clearComplianceAlternatives}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <DefaultModal
      backdropClassName="modal-v2-backdrop modal-v2-backdrop--compare-alternatives"
      cancelLabel={`${i18nBasePath}.CancelLabel`}
      className="compare-alternatives-modal"
      body={renderCompareAlternativeTable()}
      hidePrimaryButton
      isV2
      modalClassName="modal-v2 modal-v2--compare-alternatives"
      show={show}
      title={formatMessage(`${i18nBasePath}.Title`)}
      onCancel={closeModal}
      {...rest}
    />
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  complianceAlternatives: state.complianceAlternatives,
});

export default connect(mapStateToProps, {
  addFundComplianceAlternatives,
  addStockComplianceAlternatives,
  addFundToPortfolioById,
  addStockToPortfolioById,
  clearComplianceAlternatives,
  updatePortfolioHolding,
})(withErrorBoundary(CompareAlternatives));
